/**
 * Pick a saved template, set the kickoff date, fill in the details, apply.
 *
 * The dates are chained here as well as on the server so that editing an
 * estimate re-dates the rest of the plan as you type instead of after a round
 * trip. The server recomputes on apply and its answer is the one that gets
 * written — this copy exists only so the popup feels live.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { AlertTriangle, CalendarDays, Layers, Loader2, Trash2, X } from "lucide-react";

import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  MILESTONE_TEMPLATES_URL, MILESTONE_TEMPLATE_URL, ONBOARDED_APPLY_TEMPLATE_URL,
} from "../../constants/api";

const today = () => new Date().toISOString().slice(0, 10);

const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";

/** Mirror of the server's chain_dates: chained by default, an explicit
 *  offset starts from kickoff and does not advance the chain. */
function chain(rows, start) {
  let cursor = start;
  return rows.map(r => {
    const days = r.planned_days === "" || r.planned_days == null ? 0 : Number(r.planned_days);
    const hasOffset = r.offset_days !== "" && r.offset_days != null;
    const begins = hasOffset ? addDays(start, Number(r.offset_days)) : cursor;
    const due = days ? addDays(begins, days) : null;
    if (!hasOffset) cursor = due || begins;
    return { ...r, planned_start: begins, due_date: due };
  });
}

export default function ApplyTemplateModal({ clientId, planStartDate, onClose, onApplied }) {
  const { fetch, create, remove } = useApiMethods();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [templateId, setTemplateId] = useState(null);
  const [start, setStart]         = useState(planStartDate || today());
  const [rows, setRows]           = useState([]);
  const [existing, setExisting]   = useState(0);
  const [replace, setReplace]     = useState(false);
  const [users, setUsers]         = useState([]);
  const [busy, setBusy]           = useState(false);

  useEffect(() => {
    fetch(MILESTONE_TEMPLATES_URL, {}, setLoading).then(res => {
      if (res?.status === "success") setTemplates(res.data || []);
    });
    fetch("auth/users/simple/").then(res => {
      const list = res?.data || res?.results || [];
      setUsers(list.map(u => ({
        value: u.id,
        label: [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.user_name,
      })));
    }).catch(() => {});
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  // Server preview whenever the template or kickoff date changes. Row edits
  // after that are chained locally.
  const loadPreview = useCallback((tid, startDate) => {
    if (!tid) return;
    fetch(ONBOARDED_APPLY_TEMPLATE_URL(clientId), { template_id: tid, start_date: startDate })
      .then(res => {
        if (res?.status === "success") {
          setRows((res.items || []).map(i => ({
            milestone_name: i.milestone_name,
            planned_days:   i.planned_days ?? "",
            offset_days:    i.offset_days ?? "",
            owner_id:       i.owner_id || "",
            notes:          i.notes || "",
          })));
          setExisting(res.existing_count || 0);
        } else ToastMsg(res?.message || "Could not load the template", "error");
      });
  }, [clientId]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (templateId) loadPreview(templateId, start);
  }, [templateId, start, loadPreview]);

  const dated = useMemo(() => chain(rows, start), [rows, start]);
  const lastDue = useMemo(
    () => dated.reduce((acc, r) => (r.due_date && (!acc || r.due_date > acc) ? r.due_date : acc), null),
    [dated]
  );

  const setRow = (i, patch) =>
    setRows(prev => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const removeRow = (i) => setRows(prev => prev.filter((_, j) => j !== i));

  const addRow = () =>
    setRows(prev => [...prev, { milestone_name: "", planned_days: "", offset_days: "", owner_id: "", notes: "" }]);

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Remove this template? Plans already created from it are untouched.")) return;
    const res = await remove(MILESTONE_TEMPLATE_URL(tid));
    if (res?.status === "success") {
      ToastMsg("Template removed", "success");
      setTemplates(prev => prev.filter(t => t.id !== tid));
      if (templateId === tid) { setTemplateId(null); setRows([]); }
    } else ToastMsg(res?.message || "Failed to remove", "error");
  };

  const apply = async () => {
    if (!templateId) return ToastMsg("Choose a template", "error");
    if (!start) return ToastMsg("Set the kickoff date", "error");
    const usable = rows.filter(r => (r.milestone_name || "").trim());
    if (!usable.length) return ToastMsg("Add at least one task", "error");

    setBusy(true);
    const res = await create(ONBOARDED_APPLY_TEMPLATE_URL(clientId), {
      template_id: templateId,
      start_date: start,
      replace_existing: replace,
      items: usable.map(r => ({
        milestone_name: r.milestone_name.trim(),
        planned_days: r.planned_days === "" ? null : Number(r.planned_days),
        offset_days: r.offset_days === "" ? null : Number(r.offset_days),
        owner_id: r.owner_id || null,
        notes: r.notes || "",
      })),
    });
    setBusy(false);
    if (res?.status === "success") {
      ToastMsg(res.message || "Plan created", "success");
      onApplied?.();
      onClose?.();
    } else ToastMsg(res?.message || "Failed to apply the template", "error");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
      onClick={onClose}>
      <div className="my-6 w-full max-w-3xl rounded-xl border border-gray-200 bg-white
                      dark:border-gray-700 dark:bg-gray-800"
        onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4
                        dark:border-gray-700">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <Layers className="h-4 w-4 text-indigo-500" />
              Build the plan from a template
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Pick a template, set the kickoff date, adjust the details, then apply.
              The plan is created as a draft and goes for approval before work starts.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 p-4">
          {/* template + kickoff */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="md:col-span-2">
              <Label>Template</Label>
              {loading ? (
                <p className="flex items-center gap-2 py-2 text-xs text-gray-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading templates…
                </p>
              ) : templates.length === 0 ? (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800
                              dark:bg-amber-500/10 dark:text-amber-300">
                  No templates saved yet. Build the milestones on a client by hand, then
                  use <b>Save as template</b> on the Milestones tab to reuse them here.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {templates.map(t => (
                    <label key={t.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 text-xs
                        ${templateId === t.id
                          ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20"
                          : "border-gray-200 dark:border-gray-600"}`}>
                      <input type="radio" name="tpl" checked={templateId === t.id}
                        onChange={() => setTemplateId(t.id)} />
                      <span className="flex-1">
                        <span className="font-medium text-gray-800 dark:text-gray-100">{t.name}</span>
                        <span className="ml-2 text-gray-400">
                          {t.item_count} task{t.item_count === 1 ? "" : "s"} · {t.total_days}d
                        </span>
                        {t.description && (
                          <span className="block text-gray-400">{t.description}</span>
                        )}
                      </span>
                      <button onClick={e => { e.preventDefault(); deleteTemplate(t.id); }}
                        className="text-gray-300 hover:text-red-500" title="Remove template">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Kickoff date *</Label>
              <Input type="date" value={start} onChange={e => setStart(e.target.value)} />
              <p className="mt-1 text-[11px] text-gray-400">
                Every date below is worked out from here.
              </p>
              {lastDue && (
                <p className="mt-2 rounded-md bg-indigo-50 px-2 py-1.5 text-[11px] text-indigo-700
                              dark:bg-indigo-500/10 dark:text-indigo-300">
                  <CalendarDays className="mr-1 inline h-3 w-3" />
                  Plan finishes <b>{fmt(lastDue)}</b>
                </p>
              )}
            </div>
          </div>

          {/* the tasks */}
          {rows.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-12 gap-2 bg-gray-50 px-3 py-2 text-[11px]
                              font-medium uppercase tracking-wide text-gray-500
                              dark:bg-gray-700/40 dark:text-gray-400">
                <span className="col-span-4">Task</span>
                <span className="col-span-1">Days</span>
                <span className="col-span-2">Owner</span>
                <span className="col-span-1">Offset</span>
                <span className="col-span-3">Schedule</span>
                <span className="col-span-1" />
              </div>
              {dated.map((r, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2 border-t
                                        border-gray-100 px-3 py-2 dark:border-gray-700">
                  <div className="col-span-4">
                    <Input value={r.milestone_name} placeholder="Task name"
                      onChange={e => setRow(i, { milestone_name: e.target.value })} />
                  </div>
                  <div className="col-span-1">
                    <Input type="number" min="0" value={r.planned_days}
                      onChange={e => setRow(i, { planned_days: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <Select showSearch allowClear className="w-full" size="small"
                      placeholder="Assign" optionFilterProp="label" options={users}
                      value={r.owner_id || undefined}
                      onChange={v => setRow(i, { owner_id: v || "" })} />
                  </div>
                  <div className="col-span-1">
                    <Input type="number" placeholder="—" value={r.offset_days}
                      title="Days after kickoff. Leave empty to run after the previous task."
                      onChange={e => setRow(i, { offset_days: e.target.value })} />
                  </div>
                  <div className="col-span-3 text-[11px] text-gray-500 dark:text-gray-400">
                    {fmt(r.planned_start)} → <b className="text-gray-700 dark:text-gray-200">
                      {fmt(r.due_date)}</b>
                    {r.offset_days !== "" && r.offset_days != null && (
                      <span className="ml-1 rounded bg-sky-100 px-1 text-sky-700
                                       dark:bg-sky-500/15 dark:text-sky-300">parallel</span>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => removeRow(i)}
                      className="text-gray-300 hover:text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="border-t border-gray-100 px-3 py-2 dark:border-gray-700">
                <Button size="sm" variant="outline" onClick={addRow}>+ Add task</Button>
              </div>
            </div>
          )}

          {existing > 0 && rows.length > 0 && (
            <label className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs
                              text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
              <input type="checkbox" className="mt-0.5" checked={replace}
                onChange={e => setReplace(e.target.checked)} />
              <span>
                <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                This client already has <b>{existing}</b> milestone{existing === 1 ? "" : "s"}.
                Tick to clear the ones that have <b>not been started</b> before applying.
                Anything already started, held or finished is kept either way — that is
                real history, not a plan.
              </span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4
                        dark:border-gray-700">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={apply} disabled={busy || !templateId || !rows.length}>
            {busy ? "Applying…" : `Create ${rows.length || ""} task${rows.length === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">{children}</label>
);
